import { Button, THEME } from "@myloc/myloc-gui";
import PropType from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useTranslate } from "../../../language/i18n";
import { setOrder } from "../../../reducers/appData/appDataActions";
import { setMessage } from "../../../reducers/dialog/dialogAction";
import clientService from "../../../services/client/clientService";
import orderService from "../../../services/order/orderService";
import pages from "../../../utils/pages";
import ScrapModal from "../../Scrap/ScrapModal";
import ClientHoldingList from "../../shared/ClientHoldingList/ClientHoldingList";
import styles from "./Holding.module.scss";
import AddModal from "./Modals/AddModal";
import ReturnModal from "./Modals/ReturnModal";

const MODAL_TYPE = {
  ADD: "ADD_MODAL",
  RETURN: "RETURN_MODAL",
  SCRAP: "SCRAP_MODAL",
  EMPTY: "",
};

function Holding({ setBreadcrumbs, receiver, referrer, fromReceiverMenu }) {
  const translate = useTranslate();
  const [selected, setSelected] = useState([]);
  const [modal, setModal] = useState("");
  const actionsDisabled = !selected.length;
  const clientId = receiver?.id;
  const [isLoadingCreateWebOrder, setIsLoadingCreateWebOrder] = useState(false);

  useEffect(() => {
    if (fromReceiverMenu) {
      setBreadcrumbs([{ text: `${translate(pages.CLIENTS.NAME)} - ${translate(pages.CLIENT_HOLDINGS.NAME)} ` }]);
    } else {
      setBreadcrumbs([
        { text: translate("SEARCH_CLIENT"), link: referrer || pages.CLIENTS.PATH },
        { text: `${translate(pages.CLIENTS.NAME)} - ${translate(pages.CLIENT_HOLDINGS.NAME)}` },
      ]);
    }
  }, [referrer, setBreadcrumbs, translate, fromReceiverMenu]);

  const handleQuantityChange = (row, num) => {
    if (!selected) return;

    if (selected.some(item => item.id === row.id)) {
      const updatedSelected = selected.filter(item => item.id !== row.id);
      updatedSelected.push({ ...row, quantity: num });
      setSelected([...updatedSelected]);
    }
  };

  const buildScrapObjects = () => {
    if (!selected.length) return [];

    let scrapArray = [];
    for (const holding of selected) {
      scrapArray.push({ balanceId: holding.id, quantity: holding.quantity });
    }
    return scrapArray;
  };

  const submitScrapping = async reasonCodeId => {
    const data = { balances: buildScrapObjects(), reasonCodeId };
    return await clientService.scrapHoldingsFromClientHoldings({ clientId, data });
  };

  const createWebOrderForClient = useCallback(async () => {
    if (isLoadingCreateWebOrder) return;

    setIsLoadingCreateWebOrder(true);
    const response = await orderService.createOrder({
      id: clientId,
      type: "CLIENT",
    });

    if (response.isOk()) {
      setMessage(translate("ORDER_CREATED") + ":" + ` ${response.data.orderNumber}`);
      setOrder(response.data);
    }

    setIsLoadingCreateWebOrder(false);
  }, [clientId, isLoadingCreateWebOrder, translate]);

  return (
    <>
      <div>
        <h3 className={styles.contentTitle}>{translate("HOLDING_LIST")}</h3>
        {clientId && (
          <ClientHoldingList
            multiSelect
            link
            onSelect={setSelected}
            selected={selected}
            clientId={clientId}
            amountChangeable
            onAmountChange={handleQuantityChange}
            referrer={referrer}
            disableAllocated
            isReceiverMenu={fromReceiverMenu}
          />
        )}
        <section className={styles.actions}>
          <div className={styles.button}>
            <Button theme={THEME.SECONDARY} onClick={() => setModal(MODAL_TYPE.ADD)} disabled={actionsDisabled}>
              {translate("ADD_AS_ACCESSORY")}
            </Button>
          </div>
          <div className={styles.button}>
            <Button theme={THEME.SECONDARY} onClick={() => setModal(MODAL_TYPE.RETURN)} disabled={actionsDisabled}>
              {translate("RETURN_TO_BASE_STORAGE")}
            </Button>
          </div>
          <div className={styles.button}>
            <Button theme={THEME.SECONDARY} onClick={() => setModal(MODAL_TYPE.SCRAP)} disabled={actionsDisabled}>
              {translate("SCRAP")}
            </Button>
          </div>
          <div className={styles.button}>
            <Button
              theme={THEME.SECONDARY}
              onClick={() => createWebOrderForClient()}
              isLoading={isLoadingCreateWebOrder}
            >
              {translate("NEW_ORDER")}
            </Button>
          </div>
        </section>
      </div>

      <AddModal
        clientId={clientId}
        visible={modal === MODAL_TYPE.ADD}
        selectedProducts={selected}
        onClose={() => setModal(MODAL_TYPE.EMPTY)}
      />
      <ReturnModal
        clientId={clientId}
        visible={modal === MODAL_TYPE.RETURN}
        selectedProducts={selected}
        onClose={() => setModal(MODAL_TYPE.EMPTY)}
      />
      <ScrapModal
        clientId={clientId}
        visible={modal === MODAL_TYPE.SCRAP}
        selectedProducts={selected}
        onClose={() => setModal(MODAL_TYPE.EMPTY)}
        onSubmit={submitScrapping}
      />
    </>
  );
}

Holding.propTypes = {
  receiver: PropType.object,
  productList: PropType.array,
  setBreadcrumbs: PropType.func,
  referrer: PropType.string,
  fromReceiverMenu: PropType.bool,
};

export default Holding;
